import baseTokens from '@dna/tokens/dist/json/value/flat/global.json'
import brandBlueTokens from '@dna/tokens/dist/json/value/flat/brands/blue.json'
import brandGreenTokens from '@dna/tokens/dist/json/value/flat/brands/green.json'
import brandTurquoiseTokens from '@dna/tokens/dist/json/value/flat/brands/turquoise.json'
import scaleSmallTokens from '@dna/tokens/dist/json/value/flat/scales/small.json'
import scaleMediumTokens from '@dna/tokens/dist/json/value/flat/scales/medium.json'
import scaleLargeTokens from '@dna/tokens/dist/json/value/flat/scales/large.json'
import themeLightTokens from '@dna/tokens/dist/json/value/flat/themes/light.json'
import themeDarkTokens from '@dna/tokens/dist/json/value/flat/themes/dark.json'

const allVariantTokens = {
	brandBlueTokens,
	brandGreenTokens,
	brandTurquoiseTokens,
	scaleSmallTokens,
	scaleMediumTokens,
	scaleLargeTokens,
	themeLightTokens,
	themeDarkTokens,
}

import { DNA } from'./visual-language'

// ✅ Defaults & Types
export declare type Variants = {
  /** Telstra brand color. */
  brand: keyof typeof DNA.variants.brands;
  /** Controls scaling for spacing & sizes. */
  scale: keyof typeof DNA.variants.scales;
  theme: keyof typeof DNA.variants.themes;
};

const DEFAULT_VARIANTS: Variants = {
	brand: 'blue',
	scale: 'medium',
	theme: 'light'
}

const DEFAULT_SELECTOR = ':root'

export const DEFAULT = {
	VARIANTS: DEFAULT_VARIANTS,
	SELECTOR: DEFAULT_SELECTOR,
	INSTANCE: {
		variants: DEFAULT_VARIANTS,
		options: { selector: DEFAULT_SELECTOR },
		setVariants: (variants: Variants) => variants,
		remove: () => {},
	}
}

export const VARIANTS_UPDATED_EVENT = 'DNA_VARIANTS_UPDATED'

// 💾 Provider Registry
type ProviderState = 'queued' | 'loading' | 'ready'

export let ROOT_PROVIDER: ProviderState | undefined = undefined
export const REGISTERED_PROVIDERS = new Map()

const registerProvider = (selector: string, state: ProviderState = 'queued') => {
	if (selector === DEFAULT.SELECTOR) {
		if (ROOT_PROVIDER) throw new Error('Root provider already registered.')
		ROOT_PROVIDER = state
		return
	}

	if (REGISTERED_PROVIDERS.has(selector)) throw new Error(`Provider with selector '${selector}' already registered.`)
	REGISTERED_PROVIDERS.set(selector, state)
}
const updateProvider = (selector: string, state: ProviderState) => {
	if (selector === DEFAULT.SELECTOR) {
		if (!ROOT_PROVIDER) return false
		ROOT_PROVIDER = state
	} else {
		if (!REGISTERED_PROVIDERS.has(selector)) return false
		REGISTERED_PROVIDERS.set(selector, state)
	}
	return true
}
const unregisterProvider = (selector: string) => selector === DEFAULT.SELECTOR
	? (ROOT_PROVIDER = undefined)
	: REGISTERED_PROVIDERS.delete(selector)

// 🏭 Factory
/**
 * A DNA Provider populates CSS vars for a given variant of design tokens.
 * 
 * You must intialise at least one default provider instance, `createDNAProvider()` which sets the CSS vars onto the `:root` element.
 * This will persist variant settings/preferences for your users between sessions.
 * 
 * You can customize the look for certain parts within your app by creating nested DNA Providers.
 * Eg. If I wanted my modal to follow the 'green' brand variant all the time: `createDNAProvider({ brand: 'green' }, { selector: '#my-modal-container' })`
 * 
 * **NOTE**: variants will only be persisted for the default `:root` provider.
 */
export const createDNAProvider = (defaultVariants = DEFAULT.VARIANTS, options = DEFAULT.INSTANCE.options) => {
	warnIfSSR()

	const { selector } = options
	const persistVariants = selector === DEFAULT.SELECTOR
  const element = document.querySelector(selector)
  if (!element) throw new Error(`Element not found for selector: ${selector}`)
	const variants = !persistVariants
		? defaultVariants
		: (getVariantsFromLocalStorage() || defaultVariants)

	registerProvider(selector)
	updateProvider(selector, 'queued');
	(window as any)[getInstanceName(selector)] = {
		variants,
		options: { selector },

		setVariants(newVariants: Variants) {
			if (isSSR()) {
				console.warn('Cannot set DNA Provider\'s variants on SSR.')
				return
			}

      const updatedVariants = { ...this.variants, ...newVariants }

			if (persistVariants) saveVariantsToLocalStorage(updatedVariants)
			this.variants = updatedVariants
			setAllVariantTokens(element as HTMLElement, updatedVariants)
		},

		remove() {
			unregisterProvider(selector)
			delete (window as any)[getInstanceName(selector)]
		}
	}

	if (!updateProvider(selector, 'loading')) return
	if (persistVariants) saveVariantsToLocalStorage(variants)
	setBaseTokens()
	setAllVariantTokens(element as HTMLElement, variants)
	updateProvider(selector, 'ready')
}

export const getDNAProvider = (selector = DEFAULT.SELECTOR) => isSSR()
	? DEFAULT.INSTANCE
	: (window as any)[getInstanceName(selector)]

// 🔧 Helpers
const getInstanceName = (selector: string) => `__DNAProvider_${selector.toLowerCase().replace(/:|#|\./g, '')}`

const setAllVariantTokens = (element: HTMLElement, variants: Variants) => {
	Object.entries(variants).forEach(([variant, type]) => {
    const key = `${variant}${capitalize(type)}Tokens` as keyof typeof allVariantTokens
    setTokens(
      element,
      allVariantTokens[key]
    )
  })

	dispatchUpdatedEvent(element)
}

const LOCAL_STORAGE_KEY = 'DNAProvider'
const saveVariantsToLocalStorage = (variants: Variants) => window.localStorage.setItem(
	LOCAL_STORAGE_KEY,
	JSON.stringify({ variants })
)
const getVariantsFromLocalStorage = () => {
	const state = window.localStorage.getItem(LOCAL_STORAGE_KEY)
	return !state
		? undefined
		: JSON.parse(state).variants
}

const setBaseTokens = () => {
  const [[egTokenKey, egTokenVal]] = Object.entries(baseTokens)
  const el = document.querySelector(DEFAULT.SELECTOR) as HTMLElement
	const egTokenExists = el.style
		.getPropertyValue(egTokenKey) === egTokenVal

	if (egTokenExists) return
	setTokens(el, baseTokens)
}

const setTokens = (element: HTMLElement, tokens: Object) => {
	Object.entries(tokens).forEach(
		([key, value]) => element.style.setProperty(key, value)
	)
}

const warnIfSSR = () => {
	if (!isSSR()) return
	console.warn("window is 'undefined': DNA's Provider cannot run on SSR.")
}

const dispatchUpdatedEvent = (element: HTMLElement) => {
	try {
		element.dispatchEvent(new Event(VARIANTS_UPDATED_EVENT))
	} catch (err) {
		if (!err.message.includes("Cannot read property 'dispatchEvent' of null")) throw err
		console.warn('Cannot dispatch updated variants event on missing element.')
	}
}

const isSSR = () => typeof window === 'undefined'
const capitalize = (str: string) => `${str.slice(0, 1).toUpperCase()}${str.slice(1)}`
